import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError, type AxiosError } from 'axios'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'
import type { IApiError } from '~/types/api'
import type { IAppManagement } from '~/types/application-management'
import type { ICluster } from '~/types/cluster'

import { createFile, uploadFile } from '~/api/file'
import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import CheckboxInput from '~/components/Inputs/ReactHookForm/CheckboxInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Label from '~/components/Label'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { keys as appsManagementKeys, useSetAppMutation } from '~/queries/application-management'
import { useClustersListQuery } from '~/queries/clusters'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const MODAL_ID = 'set-app-modal'

const accountKeyOptions = [
  { label: 'MWM', value: 'mwm' },
  { label: 'Alma', value: 'alma' },
  { label: 'Approver', value: 'approver' },
]

interface IAppManagementFormValues {
  android?: {
    account_key?: IOption
    app_key?: string
    app_token?: string
    bundle_id?: string
    firebase_project_id?: string
    private_store_id?: string
  }
  android_enabled: boolean
  app_key: string
  default_cluster_id: IOption | null
  default_display_name: string
  default_mwm_iap_pack_id?: string
  gcp_id?: string
  hms?: {
    account_key?: IOption | null
    app_key?: string
    app_token?: string
    bundle_id?: string
    firebase_project_id?: string
    store_id?: number
    subscription_key?: string
  }
  hms_enabled: boolean
  icon_file_id?: string
  ios?: {
    account_key?: IOption | null
    app_key?: string
    app_token?: string
    bundle_id?: string
    firebase_project_id?: string
    sku?: string
    store_id?: number
    subscription_key?: string
  }
  ios_enabled: boolean
  is_publishing: boolean
  is_unity: boolean
  lokalise_project_id?: string
  prefix: string
  privacy_policy_url?: string
  support_email?: string
  terms_of_services_url?: string
  web?: {
    account_key?: IOption | null
    app_key?: string
    app_token?: string
    bundle_id?: string
    firebase_project_id?: string
    url?: string
  }
  web_enabled: boolean
}

const validationSchema = Yup.object({
  android: Yup.object({
    account_key: Yup.object({
      label: Yup.string().ensure(),
      value: Yup.string().ensure(),
    }).when('android_enabled', {
      is: true,
      then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
    }),
    app_token: Yup.string(),
    bundle_id: Yup.string(),
    firebase_project_id: Yup.string(),
    private_store_id: Yup.string(),
  }),
  android_enabled: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  app_key: Yup.string().required(FORM_ERRORS.REQUIRED),
  default_cluster_id: Yup.object({
    label: Yup.string().ensure(),
    value: Yup.string().ensure(),
  }).nullable(),
  default_display_name: Yup.string().required(FORM_ERRORS.REQUIRED),
  default_mwm_iap_pack_id: Yup.string(),
  gcp_id: Yup.string(),
  hms: Yup.object({
    account_key: Yup.object({
      label: Yup.string().ensure(),
      value: Yup.string().ensure(),
    }).when('hms_enabled', {
      is: true,
      then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
    }),
    app_token: Yup.string(),
    bundle_id: Yup.string(),
    firebase_project_id: Yup.string(),
    sku: Yup.string(),
    store_id: Yup.number(),
    subscription_key: Yup.string(),
  }),
  hms_enabled: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  icon_file_id: Yup.string(),
  ios: Yup.object({
    account_key: Yup.object({
      label: Yup.string().ensure(),
      value: Yup.string().ensure(),
    }).when('ios_enabled', {
      is: true,
      then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
    }),
    app_token: Yup.string(),
    bundle_id: Yup.string(),
    firebase_project_id: Yup.string(),
    sku: Yup.string(),
    store_id: Yup.number(),
    subscription_key: Yup.string(),
  }),
  ios_enabled: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  is_publishing: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  is_unity: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  lokalise_project_id: Yup.string(),
  prefix: Yup.string().required(FORM_ERRORS.REQUIRED),
  privacy_policy_url: Yup.string(),
  support_email: Yup.string(),
  terms_of_services_url: Yup.string(),
  web: Yup.object({
    account_key: Yup.object({
      label: Yup.string().ensure(),
      value: Yup.string().ensure(),
    }).when('web_enabled', {
      is: true,
      then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
    }),
    app_token: Yup.string(),
    bundle_id: Yup.string(),
    firebase_project_id: Yup.string(),
    url: Yup.string(),
  }),
  web_enabled: Yup.boolean().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  application?: IAppManagement
  onClose: VoidFunction
  section?: string
}

const SetAppForm = ({ application, onClose, section }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [file, setFile] = useState<FileWithPath>()

  const [blob, setBlob] = useState<string>()

  const { data: clusters } = useClustersListQuery()

  const defaultCluster = application
    ? clusters?.data?.find(({ cluster_id }) => cluster_id === application.default_cluster_id)
    : null

  const clusterOptions = useMemo(
    () =>
      clusters?.data?.map(({ cluster_id, name }: ICluster) => ({
        label: name || 'Unknown cluster',
        value: cluster_id,
      })) || [],
    [clusters?.data]
  )

  const initialValues: IAppManagementFormValues = {
    android: application?.android
      ? {
          ...application.android,
          account_key: accountKeyOptions.find(({ value }) => value === application.android?.account_key),
        }
      : undefined,
    android_enabled: !!application?.android,
    app_key: application?.app_key || '',
    default_cluster_id: defaultCluster
      ? { label: defaultCluster.name || 'Unknown cluster', value: defaultCluster.cluster_id }
      : null,
    default_display_name: application?.default_display_name || '',
    default_mwm_iap_pack_id: application?.default_mwm_iap_pack_id || '',
    gcp_id: application?.gcp_id || '',
    hms: application?.hms
      ? {
          ...application.hms,
          account_key: accountKeyOptions.find(({ value }) => value === application.hms?.account_key),
        }
      : undefined,
    hms_enabled: !!application?.hms,
    icon_file_id: application?.icon_file_id || '',
    ios: application?.ios
      ? {
          ...application.ios,
          account_key: accountKeyOptions.find(({ value }) => value === application.ios?.account_key),
        }
      : undefined,
    ios_enabled: !!application?.ios,
    is_publishing: application?.is_publishing || false,
    is_unity: application?.is_unity || false,
    lokalise_project_id: application?.lokalise_project_id || '',
    prefix: application?.prefix || '',
    privacy_policy_url: application?.privacy_policy_url || '',
    support_email: application?.support_email || '',
    terms_of_services_url: application?.terms_of_services_url || '',
    web: application?.web
      ? {
          ...application.web,
          account_key: accountKeyOptions.find(({ value }) => value === application.web?.account_key),
        }
      : undefined,
    web_enabled: !!application?.web,
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setFocus,
    setValue,
    watch,
  } = useForm<IAppManagementFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver<IAppManagementFormValues>(validationSchema),
  })

  const iosEnabled = watch('ios_enabled')
  const androidEnabled = watch('android_enabled')
  const hmsEnabled = watch('hms_enabled')
  const webEnabled = watch('web_enabled')

  /** Queries & Mutations */

  const { mutateAsync: setApp } = useSetAppMutation()

  /** Effects */

  useEffect(() => {
    if (section && section !== 'general') {
      const element = document.getElementById(section)

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      setFocus('app_key')
    }
  }, [setFocus, section])

  useEffect(() => {
    if (application?.icon_file_id) {
      setBlob(`${FASTLY_PATH}${application.icon_file_id}`)
    }
  }, [application?.icon_file_id])

  useEffect(() => {
    if (!iosEnabled) {
      const iosFields = [
        'ios.account_key',
        'ios.app_key',
        'ios.app_token',
        'ios.bundle_id',
        'ios.firebase_project_id',
        'ios.sku',
        'ios.store_id',
        'ios.subscription_key',
        'ios',
      ]

      iosFields.forEach((key) => {
        if (key === 'ios.account_key') {
          setValue(key as keyof IAppManagementFormValues, { label: '', value: '' })
        }
        setValue(key as keyof IAppManagementFormValues, undefined)
      })
    } else if (!androidEnabled) {
      const androidFields = [
        'android.account_key',
        'android.app_key',
        'android.app_token',
        'android.bundle_id',
        'android.firebase_project_id',
        'android.private_store_id',
        'android',
      ]

      androidFields.forEach((key) => {
        if (key === 'android.account_key') {
          setValue(key as keyof IAppManagementFormValues, { label: '', value: '' })
        }
        setValue(key as keyof IAppManagementFormValues, undefined)
      })
    } else if (!hmsEnabled) {
      const hmsFields = [
        'hms.account_key',
        'hms.app_key',
        'hms.app_token',
        'hms.bundle_id',
        'hms.firebase_project_id',
        'hms.store_id',
        'hms.subscription_key',
        'hms',
      ]

      hmsFields.forEach((key) => {
        if (key === 'hms.account_key') {
          setValue(key as keyof IAppManagementFormValues, { label: '', value: '' })
        }
        setValue(key as keyof IAppManagementFormValues, undefined)
      })
    } else if (!webEnabled) {
      const webFields = [
        'web.account_key',
        'web.app_key',
        'web.app_token',
        'web.bundle_id',
        'web.firebase_project_id',
        'web.url',
        'web',
      ]

      webFields.forEach((key) => {
        if (key === 'web.account_key') {
          setValue(key as keyof IAppManagementFormValues, { label: '', value: '' })
        }
        setValue(key as keyof IAppManagementFormValues, undefined)
      })
    }
  }, [androidEnabled, hmsEnabled, iosEnabled, setValue, webEnabled])

  /** Handlers */

  const handleFormSubmit = async (values: IAppManagementFormValues) => {
    const { android, android_enabled, hms, hms_enabled, ios, ios_enabled, web, web_enabled } = values

    if (!android_enabled && !hms_enabled && !ios_enabled && !web_enabled) {
      displayToast('You must specify at least one device config!', 'error')
      return
    }

    if (
      (android_enabled && !android?.account_key?.value) ||
      (hms_enabled && !hms?.account_key?.value) ||
      (ios_enabled && !ios?.account_key?.value) ||
      (web_enabled && !web?.account_key?.value)
    ) {
      displayToast('An account key is missing for one of your configs!', 'error')
      return
    }

    const androidConfig = android_enabled ? { ...android, account_key: android?.account_key?.value } : null
    const iosConfig = ios_enabled ? { ...ios, account_key: ios?.account_key?.value } : null
    const hmsConfig = hms_enabled ? { ...hms, account_key: hms?.account_key?.value } : null
    const webConfig = web_enabled ? { ...web, account_key: web?.account_key?.value } : null

    try {
      let fileId = application?.icon_file_id

      if (file) {
        const { id, upload_url } = await createFile({
          mimeType: file.type,
          name: file.name,
          size: file.size,
        })
        await uploadFile({ file, mimeType: file.type, uploadUrl: upload_url })
        fileId = id
      }

      await setApp({
        ...values,
        android: androidConfig,
        default_cluster_id: values.default_cluster_id?.value,
        hms: hmsConfig,
        icon_file_id: fileId,
        ios: iosConfig,
        web: webConfig,
      })

      await queryClient.invalidateQueries({ queryKey: appsManagementKeys.list() })
      await queryClient.invalidateQueries({ queryKey: appsManagementKeys.details(values.app_key) })
      displayToast(`Application successfully  ${!!application ? 'updated' : 'created'}`, 'success')
      onClose()
    } catch (e) {
      if (isAxiosError(e)) {
        const apiError = e as AxiosError<IApiError>

        switch (apiError.response?.data.kind) {
          case 'missing_device_config':
            displayToast('You must specify at least one device config!', 'error')
            break
          default:
            captureException(e)
            displayToast(`The application could not be ${!!application ? 'updated' : 'created'}`, 'error')
        }
      } else {
        captureException(e)
        displayToast(`The application could not be ${!!application ? 'updated' : 'created'}`, 'error')
      }
    }
  }

  const handleDrop = (file: FileWithPath[]) => {
    setFile(file[0])
    setBlob(URL.createObjectURL(file[0]))
  }

  const handleRemoveFile = () => {
    setFile(undefined)
    setBlob(undefined)
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="set-app-modal-body">
        <div id="general">
          <RequiredInformation />
          <div>
            <TextInput
              {...register('app_key')}
              error={errors.app_key?.message}
              label="App key"
              placeholder="Type a key"
              required
            />
            <div className="icon-file-input">
              <Label className="icon-file-input__label" label="Icon" />
              <Dropzone
                accept={{ 'image/*': [] }}
                className={classNames('dropzone-input', { 'dropzone-input--hidden': file && blob })}
                multiple={false}
                onDrop={handleDrop}
              />
              {blob ? (
                <FilePreview
                  className="small"
                  mimeType="image/*"
                  name={file?.path || ''}
                  onRemove={handleRemoveFile}
                  src={blob}
                  withQualityIdentifier={false}
                />
              ) : null}
            </div>
          </div>
          <TextInput
            {...register('default_display_name')}
            error={errors.default_display_name?.message}
            label="Display name"
            placeholder="Type a display name"
            required
          />
          <TextInput
            {...register('prefix')}
            error={errors.prefix?.message}
            label="Prefix"
            placeholder="Type a prefix"
            required
          />
          <TextInput {...register('gcp_id')} error={errors.gcp_id?.message} label="GCP ID" placeholder="" />
          <TextInput
            {...register('lokalise_project_id')}
            error={errors.lokalise_project_id?.message}
            label="Lokalise ID"
            placeholder="Type the lokalise ID"
          />
          <Controller
            control={control}
            name="default_cluster_id"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.default_cluster_id?.message}
                label="Default Cluster"
                options={clusterOptions}
                placeholder="Select the default cluster"
              />
            )}
          />
          <CheckboxInput control={control} label="Is Unity technology" labelPosition="right" name="is_unity" />
          <CheckboxInput control={control} label="Is publishing" labelPosition="right" name="is_publishing" />
          <TextInput
            {...register('support_email')}
            error={errors.support_email?.message}
            label="Support email"
            placeholder=""
          />
          <TextInput
            {...register('terms_of_services_url')}
            error={errors.terms_of_services_url?.message}
            label="Terms of services URL"
            placeholder=""
          />
          <TextInput
            {...register('privacy_policy_url')}
            error={errors.privacy_policy_url?.message}
            label="Privacy policy URL"
            placeholder=""
          />

          {/** IOS */}
          <div className="device-category">
            <div className="device-category__title" id="ios-configuration">
              <CheckboxInput
                control={control}
                label="Enabled IOS configuration"
                labelPosition="right"
                name="ios_enabled"
              />
            </div>
            <Controller
              control={control}
              name="ios.account_key"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  error={errors.ios?.account_key?.message}
                  isDisabled={!iosEnabled}
                  label="Account Key"
                  options={accountKeyOptions}
                />
              )}
            />
            <TextInput
              {...register('ios.bundle_id')}
              disabled={!iosEnabled}
              error={errors.ios?.bundle_id?.message}
              label="Bundle ID"
              placeholder=""
            />
            <TextInput
              {...register('ios.app_token')}
              disabled={!iosEnabled}
              error={errors.ios?.app_token?.message}
              label="App Token"
              placeholder=""
            />
            <TextInput
              {...register('ios.firebase_project_id')}
              disabled={!iosEnabled}
              error={errors.ios?.firebase_project_id?.message}
              label="Firebase Project ID"
              placeholder=""
            />
            <TextInput
              {...register('ios.store_id')}
              disabled={!iosEnabled}
              error={errors.ios?.store_id?.message}
              label="Store ID"
              placeholder=""
            />
            <TextInput
              {...register('ios.sku')}
              disabled={!iosEnabled}
              error={errors.ios?.sku?.message}
              label="SKU"
              placeholder=""
            />
            <TextInput
              {...register('ios.subscription_key')}
              disabled={!iosEnabled}
              error={errors.ios?.subscription_key?.message}
              label="Subscription Key"
              placeholder=""
            />
          </div>

          {/** ANDROID */}
          <div className="device-category">
            <div className="device-category__title" id="android-configuration">
              <CheckboxInput
                control={control}
                label="Enabled Android configuration"
                labelPosition="right"
                name="android_enabled"
              />
            </div>
            <Controller
              control={control}
              name="android.account_key"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  error={errors.android?.account_key?.message}
                  isDisabled={!androidEnabled}
                  label="Account Key"
                  options={accountKeyOptions}
                />
              )}
            />
            <TextInput
              {...register('android.bundle_id')}
              disabled={!androidEnabled}
              error={errors.android?.bundle_id?.message}
              label="Bundle ID"
              placeholder=""
            />
            <TextInput
              {...register('android.app_token')}
              disabled={!androidEnabled}
              error={errors.android?.app_token?.message}
              label="App Token"
              placeholder=""
            />
            <TextInput
              {...register('android.firebase_project_id')}
              disabled={!androidEnabled}
              error={errors.android?.firebase_project_id?.message}
              label="Firebase Project ID"
              placeholder=""
            />
            <TextInput
              {...register('android.private_store_id')}
              disabled={!androidEnabled}
              error={errors.android?.private_store_id?.message}
              label="Private Store ID"
              placeholder=""
            />
          </div>

          {/** HMS */}
          <div className="device-category">
            <div className="device-category__title" id="hms-configuration">
              <CheckboxInput
                control={control}
                label="Enabled HMS configuration"
                labelPosition="right"
                name="hms_enabled"
              />
            </div>
            <Controller
              control={control}
              name="hms.account_key"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  error={errors.hms?.account_key?.message}
                  isDisabled={!hmsEnabled}
                  label="Account Key"
                  options={accountKeyOptions}
                />
              )}
            />
            <TextInput
              {...register('hms.bundle_id')}
              disabled={!hmsEnabled}
              error={errors.hms?.bundle_id?.message}
              label="Bundle ID"
              placeholder=""
            />
            <TextInput
              {...register('hms.app_token')}
              disabled={!hmsEnabled}
              error={errors.hms?.app_token?.message}
              label="App Token"
              placeholder=""
            />
            <TextInput
              {...register('hms.firebase_project_id')}
              disabled={!hmsEnabled}
              error={errors.hms?.firebase_project_id?.message}
              label="Firebase Project ID"
              placeholder=""
            />
            <TextInput
              {...register('hms.store_id')}
              disabled={!hmsEnabled}
              error={errors.hms?.store_id?.message}
              label="Store ID"
              placeholder=""
            />
            <TextInput
              {...register('hms.subscription_key')}
              disabled={!hmsEnabled}
              error={errors.hms?.subscription_key?.message}
              label="Subscription Key"
              placeholder=""
            />
          </div>

          {/** WEB */}
          <div className="device-category">
            <div className="device-category__title" id="web-configuration">
              <CheckboxInput
                control={control}
                label="Enabled Web configuration"
                labelPosition="right"
                name="web_enabled"
              />
            </div>
            <Controller
              control={control}
              name="web.account_key"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  error={errors.web?.account_key?.message}
                  isDisabled={!webEnabled}
                  label="Account Key"
                  options={accountKeyOptions}
                />
              )}
            />
            <TextInput
              {...register('web.bundle_id')}
              disabled={!webEnabled}
              error={errors.web?.bundle_id?.message}
              label="Bundle ID"
              placeholder=""
            />
            <TextInput
              {...register('web.app_token')}
              disabled={!webEnabled}
              error={errors.web?.app_token?.message}
              label="App Token"
              placeholder=""
            />
            <TextInput
              {...register('web.firebase_project_id')}
              disabled={!webEnabled}
              error={errors.web?.firebase_project_id?.message}
              label="Firebase Project ID"
              placeholder=""
            />
            <TextInput
              {...register('web.url')}
              disabled={!webEnabled}
              error={errors.web?.url?.message}
              label="URL"
              placeholder=""
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const SetAppModal = ({ application, isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal
      className="set-app-modal"
      id={MODAL_ID}
      isOpen={isOpen}
      onCloseModal={onClose}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader>{!!application ? 'Edit' : 'Create'} an application</ModalHeader>
      <SetAppForm {...props} application={application} onClose={onClose} />
    </Modal>
  )
}

export default SetAppModal
